<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData

export type AttachableType = 'natural-person' | 'estate-object' | 'company'

export interface UploadOptions {
  personId: string
  estateObjects: { id: string; label: string | null }[]
  companies: { id: string; label: string | null }[]
  documentTypes: Record<AttachableType, DocumentTypeOptionData[]>
}

const props = defineProps<{
  options: UploadOptions
}>()

/** Träger als "typ:id", damit eine einzige Auswahl Person, Objekt und Gesellschaft abdeckt. */
const target = defineModel<string | null>('target', { default: null })
const documentType = defineModel<string | null>('documentType', { default: null })

const { t } = useI18n()
const { uploading, upload } = useDocumentUpload()

const fileInput = ref<HTMLInputElement | null>(null)

const targetOptions = computed(() => [
  { value: `natural-person:${props.options.personId}`, label: t('targets.person') },
  ...props.options.estateObjects.map((object) => ({
    value: `estate-object:${object.id}`,
    label: object.label ?? t('targets.unnamedObject'),
  })),
  ...props.options.companies.map((company) => ({
    value: `company:${company.id}`,
    label: company.label ?? t('targets.unnamedCompany'),
  })),
])

const selectedTarget = computed<{ attachableType: AttachableType; attachableId: string } | null>(() => {
  if (!target.value) {
    return null
  }

  const [attachableType, attachableId] = target.value.split(':')

  return { attachableType: attachableType as AttachableType, attachableId }
})

const typeOptions = computed(() => {
  const attachableType = selectedTarget.value?.attachableType

  if (!attachableType) {
    return []
  }

  return props.options.documentTypes[attachableType].map((option) => ({
    value: option.value,
    label: option.label,
  }))
})

const ready = computed(() => Boolean(selectedTarget.value && documentType.value))

async function onFilesSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const files = [...(input.files ?? [])]

  input.value = ''

  if (!files.length || !selectedTarget.value) {
    return
  }

  await upload(
    {
      attachableType: selectedTarget.value.attachableType,
      attachableId: selectedTarget.value.attachableId,
      documentType: documentType.value,
    },
    files,
  )
}
</script>

<template>
  <section class="border-info-subtle-active bg-info-subtle rounded-xl border p-5">
    <h3 class="text-base font-bold">{{ t('title') }}</h3>
    <p class="text-ink-2 mt-1.5 max-w-[74ch] text-sm">{{ t('description') }}</p>

    <div class="mt-4 grid items-end gap-3 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
      <div>
        <label class="field-label" for="upload-target">{{ t('fields.target') }}</label>
        <FinestSelect
          id="upload-target"
          v-model="target"
          :options="targetOptions"
          :placeholder="t('placeholders.target')" />
      </div>

      <div>
        <label class="field-label" for="upload-document-type">{{ t('fields.documentType') }}</label>
        <FinestSelect
          id="upload-document-type"
          v-model="documentType"
          :options="typeOptions"
          :disabled="!selectedTarget"
          :placeholder="selectedTarget ? t('placeholders.documentType') : t('placeholders.targetFirst')" />
      </div>

      <div>
        <FinestButton
          icon="pi pi-upload"
          :label="uploading ? t('uploading') : t('choose')"
          :disabled="!ready || uploading"
          @click="fileInput?.click()" />
      </div>
    </div>

    <p class="text-ink-2 mt-3 text-xs">{{ t('hint') }}</p>

    <input
      ref="fileInput"
      type="file"
      class="hidden"
      multiple
      accept=".pdf,.jpg,.jpeg,.png"
      @change="onFilesSelected" />
  </section>
</template>

<i18n>
{
  "en": {
    "title": "Anything you upload, you do not have to type in",
    "description": "Tell us what the document is – we file it where it belongs, and you can find it there any time.",
    "fields": {
      "target": "What does it belong to?",
      "documentType": "What kind of document is it?"
    },
    "placeholders": {
      "target": "Please choose",
      "documentType": "Please choose",
      "targetFirst": "Choose what it belongs to first"
    },
    "targets": {
      "person": "Me personally",
      "unnamedObject": "Property without an address",
      "unnamedCompany": "Company without a name"
    },
    "choose": "Choose file",
    "uploading": "Uploading …",
    "hint": "PDF, JPG or PNG. Not sure what it is? Pick \"Other\" – we will take a look."
  },
  "de": {
    "title": "Alles, was du hochlädst, musst du nicht eintippen",
    "description": "Sag uns, was für ein Dokument es ist – wir legen es an der passenden Stelle ab, wo du es jederzeit wiederfindest.",
    "fields": {
      "target": "Wozu gehört es?",
      "documentType": "Was ist das für ein Dokument?"
    },
    "placeholders": {
      "target": "Bitte wählen",
      "documentType": "Bitte wählen",
      "targetFirst": "Zuerst wählen, wozu es gehört"
    },
    "targets": {
      "person": "Zu mir persönlich",
      "unnamedObject": "Objekt ohne Adresse",
      "unnamedCompany": "Gesellschaft ohne Namen"
    },
    "choose": "Datei wählen",
    "uploading": "Wird hochgeladen …",
    "hint": "PDF, JPG oder PNG. Du weißt nicht, was es ist? Wähle „Sonstiges“ – wir schauen selbst nach."
  }
}
</i18n>
